/**
 * Configuration Manager - Handles Piagnet configuration loading, validation, and saving
 */

import * as fs from "node:fs/promises";
import * as os from "node:os";
import * as path from "node:path";
import * as yaml from "yaml";
import {
	type AgentDefinition,
	type ChannelBinding,
	type ChannelConfig,
	defaultConfig,
	type PiagentConfig,
	type PiagnetConfig,
	piagnetConfigSchema,
} from "./schema.js";

/** Configuration manager options */
export interface ConfigManagerOptions {
	/** Configuration directory */
	configDir?: string;
	/** Configuration file name */
	configFile?: string;
}

/** Configuration manager */
export class ConfigManager {
	private configDir: string;
	private configFile: string;
	private config: PiagnetConfig;
	private loaded = false;

	constructor(options: ConfigManagerOptions = {}) {
		this.configDir = options.configDir || this.getDefaultConfigDir();
		this.configFile = options.configFile || "piagnet.yaml";
		this.config = { ...defaultConfig };
	}

	/** Get default configuration directory */
	private getDefaultConfigDir(): string {
		return path.join(os.homedir(), ".piagnet");
	}

	/** Expand tilde in paths */
	private expandPath(filePath: string): string {
		if (filePath.startsWith("~/")) {
			return path.join(os.homedir(), filePath.slice(2));
		}
		return filePath;
	}

	/** Get full configuration file path */
	getConfigPath(): string {
		return path.join(this.configDir, this.configFile);
	}

	/** Ensure configuration directory exists */
	async ensureConfigDir(): Promise<void> {
		await fs.mkdir(this.configDir, { recursive: true });
	}

	/** Load configuration from file */
	async load(): Promise<PiagnetConfig> {
		const configPath = this.getConfigPath();

		try {
			const content = await fs.readFile(configPath, "utf-8");
			const parsed = yaml.parse(content);

			// Validate and merge with defaults
			const result = piagnetConfigSchema.safeParse({
				...defaultConfig,
				...parsed,
			});

			if (!result.success) {
				throw new Error(`Invalid configuration: ${result.error.message}`);
			}

			this.config = result.data;

			// Expand paths
			this.config.gateway.sessionDir = this.expandPath(this.config.gateway.sessionDir);
			this.config.gateway.publicDir = this.expandPath(this.config.gateway.publicDir);

			this.loaded = true;
			return this.config;
		} catch (error) {
			if ((error as NodeJS.ErrnoException).code === "ENOENT") {
				// Config doesn't exist, use defaults through schema to get computed values
				const result = piagnetConfigSchema.safeParse(defaultConfig);
				if (result.success) {
					this.config = result.data;
				} else {
					this.config = { ...defaultConfig };
				}
				// Expand paths
				this.config.gateway.sessionDir = this.expandPath(this.config.gateway.sessionDir);
				this.config.gateway.publicDir = this.expandPath(this.config.gateway.publicDir);
				this.loaded = true;
				return this.config;
			}
			throw error;
		}
	}

	/** Save configuration to file */
	async save(): Promise<void> {
		await this.ensureConfigDir();
		const configPath = this.getConfigPath();

		const configToSave = {
			...this.config,
			gateway: {
				...this.config.gateway,
				// Convert expanded paths back to relative
				sessionDir: this.config.gateway.sessionDir.replace(os.homedir(), "~"),
				publicDir: this.config.gateway.publicDir.replace(os.homedir(), "~"),
			},
		};

		const content = yaml.stringify(configToSave, {
			indent: 2,
			lineWidth: 100,
		});

		await fs.writeFile(configPath, content, "utf-8");
	}

	/** Get current configuration */
	getConfig(): PiagnetConfig {
		if (!this.loaded) {
			throw new Error("Configuration not loaded. Call load() first.");
		}
		return this.config;
	}

	/** Update configuration */
	updateConfig(updates: Partial<PiagnetConfig>): void {
		this.config = { ...this.config, ...updates };
	}

	/** Get gateway configuration */
	getGatewayConfig(): PiagnetConfig["gateway"] {
		return this.config.gateway;
	}

	/** Get agent by ID */
	getAgent(id: string): AgentDefinition | undefined {
		return this.config.agents.find((a) => a.id === id);
	}

	/** Get default agent */
	getDefaultAgent(): AgentDefinition | undefined {
		if (this.config.defaults.agent) {
			return this.getAgent(this.config.defaults.agent);
		}
		return this.config.agents[0];
	}

	/** Add or update agent */
	upsertAgent(agent: AgentDefinition): void {
		const index = this.config.agents.findIndex((a) => a.id === agent.id);
		if (index >= 0) {
			this.config.agents[index] = agent;
		} else {
			this.config.agents.push(agent);
		}
	}

	/** Remove agent */
	removeAgent(id: string): boolean {
		const index = this.config.agents.findIndex((a) => a.id === id);
		if (index >= 0) {
			this.config.agents.splice(index, 1);
			return true;
		}
		return false;
	}

	/** Get channel by type */
	getChannel(type: string): ChannelConfig | undefined {
		return this.config.channels.find((c) => c.type === type);
	}

	/** Get enabled channels */
	getEnabledChannels(): ChannelConfig[] {
		return this.config.channels.filter((c) => c.enabled);
	}

	/** Add or update channel */
	upsertChannel(channel: ChannelConfig): void {
		const index = this.config.channels.findIndex((c) => c.type === channel.type);
		if (index >= 0) {
			this.config.channels[index] = channel;
		} else {
			this.config.channels.push(channel);
		}
	}

	/** Remove channel */
	removeChannel(type: string): boolean {
		const index = this.config.channels.findIndex((c) => c.type === type);
		if (index >= 0) {
			this.config.channels.splice(index, 1);
			return true;
		}
		return false;
	}

	/** Get bindings for agent */
	getBindingsForAgent(agentId: string): ChannelBinding[] {
		return this.config.bindings.filter((b) => b.agentId === agentId);
	}

	/** Get bindings for channel */
	getBindingsForChannel(channel: string): ChannelBinding[] {
		return this.config.bindings.filter((b) => b.channel === channel);
	}

	/** Add binding */
	addBinding(binding: ChannelBinding): void {
		// Remove existing binding for same agent/channel/bindType/bindValue
		this.config.bindings = this.config.bindings.filter(
			(b) =>
				!(
					b.agentId === binding.agentId &&
					b.channel === binding.channel &&
					b.bindType === binding.bindType &&
					b.bindValue === binding.bindValue
				),
		);
		this.config.bindings.push(binding);
	}

	/** Remove binding */
	removeBinding(agentId: string, channel: string, bindType: string, bindValue: string): boolean {
		const index = this.config.bindings.findIndex(
			(b) => b.agentId === agentId && b.channel === channel && b.bindType === bindType && b.bindValue === bindValue,
		);
		if (index >= 0) {
			this.config.bindings.splice(index, 1);
			return true;
		}
		return false;
	}

	/** Resolve route for incoming message */
	resolveRoute(channel: string, scope: string, identifier: string, guildId?: string): ChannelBinding | undefined {
		const bindings = this.config.bindings.filter((b) => b.channel === channel);

		// Priority order: peer > guild > team > account > channel > default
		const priority = ["peer", "guild", "team", "account", "channel", "default"];

		for (const bindType of priority) {
			const binding = bindings.find((b) => {
				if (b.bindType !== bindType) return false;

				switch (bindType) {
					case "peer":
						return b.bindValue === identifier;
					case "guild":
						return b.bindValue === guildId;
					case "channel":
						return b.bindValue === "*";
					case "default":
						return b.bindValue === "*";
					default:
						return b.bindValue === identifier;
				}
			});

			if (binding) return binding;
		}

		return undefined;
	}

	/** Create initial configuration file if it doesn't exist */
	async createInitialConfig(): Promise<boolean> {
		try {
			await fs.access(this.getConfigPath());
			return false; // Already exists
		} catch {
			// Create with defaults
			await this.save();
			return true;
		}
	}

	/** Validate configuration */
	validate(): { valid: boolean; errors: string[] } {
		const result = piagnetConfigSchema.safeParse(this.config);
		if (result.success) {
			return { valid: true, errors: [] };
		}
		return {
			valid: false,
			errors: result.error.errors.map((e) => `${e.path.join(".")}: ${e.message}`),
		};
	}

	/** Create config manager instance */
	static async create(options?: ConfigManagerOptions): Promise<ConfigManager> {
		const manager = new ConfigManager(options);
		await manager.load();
		return manager;
	}
}

/** Create config manager and load configuration */
export async function loadConfig(options?: ConfigManagerOptions): Promise<ConfigManager> {
	const manager = new ConfigManager(options);
	await manager.load();
	return manager;
}
